import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import GoalsCompanyGridComponent from 'webapp/components/goals/company/grid';
import GoalsCompanyTableComponent from 'webapp/components/goals/company/table';
import NoResultsGoalsComponent from 'webapp/components/no-results/goals';

import { viewSettingsNamePrefix } from 'webapp/models-custom/filter-sort/goal-company';
import type Goal from 'webapp/models-custom/goal';

export default class GoalsCompanyRouteTemplate extends Component {
	@service('account') declare accountService: Service.Account;
	@service('view-settings-manager')
	declare viewSettingsManagerService: Service.ViewSettingsManager;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('billing') declare billingService: Service.Billing;

	@tracked viewId = 'table';

	constructor(owner: Owner, args: object) {
		super(owner, args);
		const savedValues =
			this.viewSettingsManagerService.load<{ viewId: string }>(this.settingsName, true) || {};
		if (savedValues.viewId) {
			this.viewId = savedValues.viewId;
		}
	}

	get model(): Goal[] {
		return this.mmGoalsService.goalForCompanyModels;
	}

	get settingsName(): string {
		return `${viewSettingsNamePrefix}-${this.accountService.id}`;
	}

	get isTableView(): boolean {
		return this.viewId === 'table';
	}

	get showGoalsList(): boolean {
		return this.billingService.goalsEnabled && this.model.length > 0;
	}

	@action
	openView(viewId: string): void {
		this.viewId = viewId;
		this.viewSettingsManagerService.save(this.settingsName, { viewId }, true);
	}

	@action
	createNewGoal(): void {
		if (this.accountService.handleDemo()) {
			return;
		}

		const model = this.mmGoalsService.createNewModel(),
			trackingOrigin = 'goals';

		this.mmGoalsService.openCreatePopup(model, trackingOrigin);
	}

	<template>
		{{#if this.showGoalsList}}

			<div id="goals-index" class="view-{{this.viewId}}">

				{{#if this.isTableView}}
					<GoalsCompanyTableComponent
						@model={{this.model}}
						@openView={{this.openView}}
						@onAdd={{this.createNewGoal}}
					/>
				{{else}}
					<GoalsCompanyGridComponent
						@model={{this.model}}
						@openView={{this.openView}}
						@onAdd={{this.createNewGoal}}
					/>
				{{/if}}
			</div>

		{{else}}

			<NoResultsGoalsComponent @onCreate={{this.createNewGoal}} />

		{{/if}}
	</template>
}
